import { useEffect, useRef, useState } from 'react'
import type { IndustryDropdownProps } from './types'

export function IndustryDropdown({ id, industries, selectedKey, onSelect }: IndustryDropdownProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [activeKey, setActiveKey] = useState(selectedKey)
    const containerRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const listboxRef = useRef<HTMLUListElement>(null)

    const activeIndex = Math.max(
        0,
        industries.findIndex((i) => i.key === activeKey)
    )

    const open = () => {
        setActiveKey(selectedKey)
        setIsOpen(true)
    }

    const close = () => {
        setIsOpen(false)
        triggerRef.current?.focus()
    }

    const commit = (key: string) => {
        onSelect(key)
        close()
    }

    useEffect(() => {
        if (isOpen) {
            listboxRef.current?.focus()
        }
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) return

        function handlePointerDown(event: MouseEvent) {
            if (!containerRef.current?.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handlePointerDown)
        return () => document.removeEventListener('mousedown', handlePointerDown)
    }, [isOpen])

    const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            open()
        }
    }

    const handleListboxKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault()
                setActiveKey(industries[Math.min(activeIndex + 1, industries.length - 1)].key)
                break
            case 'ArrowUp':
                event.preventDefault()
                setActiveKey(industries[Math.max(activeIndex - 1, 0)].key)
                break
            case 'Home':
                event.preventDefault()
                setActiveKey(industries[0].key)
                break
            case 'End':
                event.preventDefault()
                setActiveKey(industries[industries.length - 1].key)
                break
            case 'Enter':
            case ' ':
                event.preventDefault()
                commit(activeKey)
                break
            case 'Escape':
                event.preventDefault()
                close()
                break
            case 'Tab':
                setIsOpen(false)
                break
        }
    }

    return (
        <div ref={containerRef} className="relative inline-block">
            <button
                ref={triggerRef}
                id={`${id}-trigger`}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-controls={`${id}-listbox`}
                onClick={() => (isOpen ? close() : open())}
                onKeyDown={handleTriggerKeyDown}
                className={`inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md border-2 px-2 py-0.5 text-left font-medium text-unimo-slate ${
                    isOpen ? 'border-unimo-site-grey-300 bg-white' : 'border-unimo-site-cta bg-transparent'
                }`}
            >
                {/* Every label is stacked in the same grid cell so the trigger's
                    intrinsic width is fixed to the longest one — selecting a
                    shorter industry doesn't shrink the box and reflow the line. */}
                <span className="grid">
                    {industries.map((industry) => (
                        <span
                            key={industry.key}
                            aria-hidden={industry.key !== selectedKey}
                            className={`col-start-1 row-start-1 ${industry.key === selectedKey ? '' : 'invisible'}`}
                        >
                            {industry.label}
                        </span>
                    ))}
                </span>
                <svg
                    aria-hidden="true"
                    className={`h-6 w-6 shrink-0 transition-transform ${isOpen ? 'rotate-180' : 'text-unimo-site-cta'}`}
                    viewBox="0 0 20 20"
                    fill="none"
                >
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            {isOpen && (
                <ul
                    ref={listboxRef}
                    id={`${id}-listbox`}
                    role="listbox"
                    tabIndex={-1}
                    aria-activedescendant={`${id}-option-${activeKey}`}
                    aria-labelledby={`${id}-trigger`}
                    onKeyDown={handleListboxKeyDown}
                    className="absolute left-0 z-10 mt-2 w-max min-w-full rounded-md border-2 border-unimo-site-grey-300 bg-white py-1 shadow-lg"
                >
                    {industries.map((industry) => (
                        <li
                            key={industry.key}
                            id={`${id}-option-${industry.key}`}
                            role="option"
                            aria-selected={industry.key === selectedKey}
                            onMouseEnter={() => setActiveKey(industry.key)}
                            onClick={() => commit(industry.key)}
                            className={`cursor-pointer px-4 py-2 ${
                                industry.key === activeKey ? 'bg-unimo-site-grey-100' : ''
                            }`}
                        >
                            {industry.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

export default IndustryDropdown
